import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefCallback,
} from "react";
import type Hls from "hls.js";
import {
  FaPause,
  FaPlay,
  FaVolumeHigh,
  FaVolumeXmark,
} from "react-icons/fa6";
import { MdOutlinePictureInPictureAlt } from "react-icons/md";
import { RiExpandDiagonalLine } from "react-icons/ri";
import { ClipLoader } from "react-spinners";
import {
  isHlsUrl,
  inferSourceFallbacksFromHls,
  resolveLightboxPlayUrl,
  toSameOriginMediaUrl,
  loadProgressiveSource,
} from "../utils/mediaPlayUrl";
import { attachHlsToVideo } from "../utils/attachHlsToVideo";

type Props = {
  src: string;
  className?: string;
  poster?: string;
  /** Applied to the video element (wheel-zoom). */
  mediaStyle?: CSSProperties;
  stageRef?: RefCallback<HTMLDivElement | null>;
  /** True while user drags seek/volume — parent carousel should disable swipe. */
  onControlsGestureChange?: (active: boolean) => void;
};

function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "00:00";
  const s = Math.floor(sec % 60);
  const m = Math.floor(sec / 60) % 60;
  const h = Math.floor(sec / 3600);
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function getBufferedEnd(video: HTMLVideoElement): number {
  try {
    const { buffered, currentTime } = video;
    if (!buffered.length) return 0;
    for (let i = 0; i < buffered.length; i++) {
      const start = buffered.start(i);
      const end = buffered.end(i);
      if (currentTime >= start && currentTime <= end + 0.35) return end;
    }
    let best = 0;
    for (let i = 0; i < buffered.length; i++) {
      const end = buffered.end(i);
      if (buffered.start(i) >= currentTime - 0.05) return end;
      if (end > best) best = end;
    }
    return best;
  } catch {
    return 0;
  }
}

/**
 * Lightbox video: top actions (play / fullscreen / PiP / HD),
 * bottom bar = volume above progress.
 */
export default function LightboxVideoPlayer({
  src,
  className,
  poster,
  mediaStyle,
  stageRef,
  onControlsGestureChange,
}: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const stageNodeRef = useRef<HTMLDivElement | null>(null);
  const hideTimer = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const seekingRef = useRef(false);
  const progressFillRef = useRef<HTMLDivElement | null>(null);
  const bufferFillRef = useRef<HTMLDivElement | null>(null);
  const knobRef = useRef<HTMLDivElement | null>(null);
  const currentLabelRef = useRef<HTMLSpanElement | null>(null);
  const remainLabelRef = useRef<HTMLSpanElement | null>(null);
  const bufferShownRef = useRef(0);
  const bufferRafRef = useRef<number | null>(null);

  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showUi, setShowUi] = useState(true);
  const [seeking, setSeeking] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playError, setPlayError] = useState<string | null>(null);
  const [videoSize, setVideoSize] = useState({ w: 0, h: 0 });
  const [loading, setLoading] = useState(true);
  const fallbackTriedRef = useRef(false);
  /** Remount <video> when leaving broken HLS/MSE (esp. 2nd video in Yandex). */
  const [playerEpoch, setPlayerEpoch] = useState(0);
  const progressiveAfterRemountRef = useRef(false);
  const videoKey = `${src || "empty"}::${playerEpoch}`;

  const setStageNode = useCallback(
    (node: HTMLDivElement | null) => {
      stageNodeRef.current = node;
      stageRef?.(node);
    },
    [stageRef],
  );

  const syncFullscreenState = useCallback(() => {
    const el = wrapRef.current;
    const fs =
      document.fullscreenElement ||
      // @ts-expect-error webkit
      document.webkitFullscreenElement;
    setIsFullscreen(!!el && (fs === el || (!!fs && el.contains(fs as Node))));
  }, []);

  /** Scale video to fill the fullscreen stage (beats slick/intrinsic size). */
  const fitVideoToStage = useCallback(() => {
    const stage = stageNodeRef.current;
    const video = videoRef.current;
    if (!stage || !video) return;
    const sw = stage.clientWidth;
    const sh = stage.clientHeight;
    if (sw < 2 || sh < 2) return;
    const vw = video.videoWidth || 9;
    const vh = video.videoHeight || 16;
    const scale = Math.min(sw / vw, sh / vh);
    const w = Math.max(1, Math.floor(vw * scale));
    const h = Math.max(1, Math.floor(vh * scale));
    video.style.setProperty("width", `${w}px`, "important");
    video.style.setProperty("height", `${h}px`, "important");
    video.style.setProperty("max-width", "none", "important");
    video.style.setProperty("max-height", "none", "important");
    video.style.setProperty("object-fit", "contain", "important");
  }, []);

  const clearVideoFit = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.style.removeProperty("width");
    video.style.removeProperty("height");
    video.style.removeProperty("max-width");
    video.style.removeProperty("max-height");
    video.style.removeProperty("object-fit");
  }, []);

  useEffect(() => {
    document.addEventListener("fullscreenchange", syncFullscreenState);
    document.addEventListener(
      "webkitfullscreenchange",
      syncFullscreenState as any,
    );
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreenState);
      document.removeEventListener(
        "webkitfullscreenchange",
        syncFullscreenState as any,
      );
    };
  }, [syncFullscreenState]);

  useEffect(() => {
    if (!isFullscreen) {
      clearVideoFit();
      return;
    }
    fitVideoToStage();
    const video = videoRef.current;
    const stage = stageNodeRef.current;
    const onMeta = () => fitVideoToStage();
    video?.addEventListener("loadedmetadata", onMeta);
    window.addEventListener("resize", fitVideoToStage);
    const ro =
      typeof ResizeObserver !== "undefined" && stage
        ? new ResizeObserver(() => fitVideoToStage())
        : null;
    if (stage && ro) ro.observe(stage);
    // Layout after fullscreen paint
    const t = window.setTimeout(fitVideoToStage, 50);
    return () => {
      window.clearTimeout(t);
      video?.removeEventListener("loadedmetadata", onMeta);
      window.removeEventListener("resize", fitVideoToStage);
      ro?.disconnect();
    };
  }, [isFullscreen, src, fitVideoToStage, clearVideoFit]);

  const clearHideTimer = () => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const bumpUi = useCallback((keepVisible = false) => {
    setShowUi(true);
    clearHideTimer();
    if (keepVisible) return;
    const v = videoRef.current;
    if (v && !v.paused) {
      hideTimer.current = window.setTimeout(() => setShowUi(false), 2800);
    }
  }, []);

  useEffect(() => {
    progressiveAfterRemountRef.current = false;
    fallbackTriedRef.current = false;
    setPlayerEpoch(0);
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    let hls: Hls | null = null;
    let revoked = false;
    let blobObjectUrl: string | null = null;
    let frameWatch: number | null = null;
    setPlayError(null);
    setVideoSize({ w: 0, h: 0 });
    setLoading(true);

    const log = (message: string) => {
      console.info(`[Video] ${message}`);
    };

    const markReady = () => {
      setLoading(false);
    };

    const destroyHls = () => {
      if (hls) {
        try {
          hls.stopLoad();
        } catch {
          /* */
        }
        try {
          hls.detachMedia();
        } catch {
          /* */
        }
        try {
          hls.destroy();
        } catch {
          /* */
        }
        hls = null;
      }
    };

    const revokeBlob = () => {
      if (blobObjectUrl) {
        try {
          URL.revokeObjectURL(blobObjectUrl);
        } catch {
          /* */
        }
        blobObjectUrl = null;
      }
    };

    const playProgressiveChain = async () => {
      if (revoked) return false;
      setLoading(true);
      log("Запуск progressive");

      // Let previous MSE/GPU decoder fully release after remount
      await new Promise<void>((r) => window.setTimeout(r, 120));
      if (revoked) return false;

      const ordered = (
        isHlsUrl(src) ? inferSourceFallbacksFromHls(src) : [src]
      ).map((u) => toSameOriginMediaUrl(u));

      for (const candidate of ordered) {
        if (revoked) return false;
        if (/playback\.webm$/i.test(candidate)) log("Пробуем webm");
        else if (/playback\.mp4$/i.test(candidate)) log("Пробуем mp4");
        else log("Пробуем исходник");

        const result = await loadProgressiveSource(video, candidate, log);
        if (revoked) return false;
        if (!("objectUrl" in result)) continue;
        revokeBlob();
        blobObjectUrl = result.objectUrl;

        setPlayError(null);
        setVideoSize({ w: video.videoWidth, h: video.videoHeight });
        setDuration(video.duration || 0);
        markReady();
        log("Воспроизведение запущено");
        try {
          video.muted = true;
          await video.play();
          video.muted = muted;
          setPlaying(true);
        } catch {
          setPlayError("Нажмите Play для воспроизведения");
          setShowUi(true);
        }
        return true;
      }
      return false;
    };

    const failAll = () => {
      log("Не удалось открыть видео");
      setLoading(false);
      setPlayError("Не удалось воспроизвести видео");
    };

    /** Fresh <video> then progressive — same node after HLS often stays black in Yandex. */
    const requestProgressiveRemount = () => {
      if (revoked || fallbackTriedRef.current) return;
      fallbackTriedRef.current = true;
      destroyHls();
      revokeBlob();
      progressiveAfterRemountRef.current = true;
      log("Пересоздание плеера для progressive");
      setPlayerEpoch((n) => n + 1);
    };

    const watchForPaintedFrame = () => {
      if (frameWatch != null) return;
      const start = Date.now();
      const tick = () => {
        if (revoked || fallbackTriedRef.current) return;
        if (
          typeof (video as HTMLVideoElement & {
            requestVideoFrameCallback?: (cb: () => void) => number;
          }).requestVideoFrameCallback === "function"
        ) {
          (
            video as HTMLVideoElement & {
              requestVideoFrameCallback: (cb: () => void) => number;
            }
          ).requestVideoFrameCallback(() => {
            if (!revoked) markReady();
          });
        }
        const w = video.videoWidth;
        const elapsed = Date.now() - start;
        // Still loading MSE — don't remount yet (HLS size timeout handles empty)
        if (w < 2) {
          if (elapsed > 12000) {
            log("HLS без кадра — fallback");
            requestProgressiveRemount();
            return;
          }
          frameWatch = window.setTimeout(tick, 400);
          return;
        }
        // Has size: keep if time advances; else black decode after ~4s
        if (video.currentTime >= 0.15) {
          markReady();
          return;
        }
        if (elapsed > 4500) {
          log("HLS без кадра — fallback");
          requestProgressiveRemount();
          return;
        }
        frameWatch = window.setTimeout(tick, 400);
      };
      frameWatch = window.setTimeout(tick, 400);
    };

    const onMeta = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      setVideoSize({ w, h });
      setDuration(video.duration || 0);
      if (w >= 2 && h >= 2) {
        markReady();
        log("Видео готово");
        watchForPaintedFrame();
      } else if (video.duration > 0) {
        // Duration without size → often black HLS on Yandex
        watchForPaintedFrame();
      }
    };
    const onVideoError = () => {
      if (fallbackTriedRef.current || revoked) return;
      log("Ошибка декодирования");
      requestProgressiveRemount();
    };

    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("error", onVideoError);

    const setup = async () => {
      log("Видео инициализировано");

      if (progressiveAfterRemountRef.current) {
        progressiveAfterRemountRef.current = false;
        const ok = await playProgressiveChain();
        if (!ok && !revoked) failAll();
        return;
      }

      let playUrl = src;
      if (isHlsUrl(src)) {
        playUrl = resolveLightboxPlayUrl(src).url;
      }
      if (revoked) return;

      if (isHlsUrl(playUrl)) {
        log("Запуск HLS");
        const attached = attachHlsToVideo(video, playUrl, undefined, {
          onLog: log,
          onFatal: () => requestProgressiveRemount(),
        });
        hls = attached.hls;

        // Give MSE time to append init+first fragment before declaring dead.
        // Earlier 4s cut often destroyed a still-loading player (media-internals:
        // ChunkDemuxer only, then Destroyed @ ~4.7s).
        window.setTimeout(() => {
          if (revoked || fallbackTriedRef.current) return;
          const hasSize = video.videoWidth >= 2;
          const hasBuffered =
            video.buffered.length > 0 &&
            video.buffered.end(0) - video.buffered.start(0) > 0.2;
          if (!hasSize && !hasBuffered) {
            log("HLS таймаут без размера");
            requestProgressiveRemount();
          } else if (hasSize) {
            markReady();
          }
        }, 10000);

        try {
          video.muted = true;
          await video.play();
          if (!fallbackTriedRef.current) video.muted = muted;
          watchForPaintedFrame();
        } catch (e) {
          if ((e as { name?: string })?.name === "AbortError") return;
          if (fallbackTriedRef.current) return;
          log("Автозапуск заблокирован — нажмите Play");
          setPlayError("Нажмите Play для воспроизведения");
          setPlaying(false);
          setShowUi(true);
          setLoading(false);
        }
        return;
      }

      const ok = await playProgressiveChain();
      if (!ok && !revoked) failAll();
    };

    void setup();

    return () => {
      revoked = true;
      if (frameWatch != null) window.clearTimeout(frameWatch);
      clearHideTimer();
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("error", onVideoError);
      destroyHls();
      revokeBlob();
      video.removeAttribute("src");
      video.srcObject = null;
      try {
        video.load();
      } catch {
        /* */
      }
    };
  }, [src, playerEpoch]);

  const paintProgress = useCallback((t: number, dur: number) => {
    const pct = dur > 0 ? Math.min(100, Math.max(0, (t / dur) * 100)) : 0;
    if (progressFillRef.current) {
      progressFillRef.current.style.width = `${pct}%`;
    }
    if (knobRef.current) {
      knobRef.current.style.left = `${pct}%`;
    }
    if (currentLabelRef.current) {
      currentLabelRef.current.textContent = formatClock(t);
    }
    if (remainLabelRef.current) {
      remainLabelRef.current.textContent = `−${formatClock(Math.max(0, dur - t))}`;
    }
  }, []);

  const paintBuffer = useCallback((shownSec: number, dur: number) => {
    const pct = dur > 0 ? Math.min(100, Math.max(0, (shownSec / dur) * 100)) : 0;
    if (bufferFillRef.current) {
      bufferFillRef.current.style.width = `${pct}%`;
    }
  }, []);

  /** Smoothly chase real buffered end (avoids chunky jumps). */
  const smoothBufferTick = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      bufferRafRef.current = requestAnimationFrame(smoothBufferTick);
      return;
    }
    const dur = video.duration || 0;
    const target = getBufferedEnd(video);
    let shown = bufferShownRef.current;

    if (target < shown - 0.35) {
      // seek backward / new range — snap down
      shown = target;
    } else {
      // ease toward target (~smooth fill)
      shown += (target - shown) * 0.1;
      if (Math.abs(target - shown) < 0.02) shown = target;
    }

    bufferShownRef.current = shown;
    paintBuffer(shown, dur);
    bufferRafRef.current = requestAnimationFrame(smoothBufferTick);
  }, [paintBuffer]);

  const stopRaf = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const startRaf = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    stopRaf();
    let lastStateAt = 0;
    const tick = () => {
      if (!seekingRef.current) {
        const t = video.currentTime;
        const dur = video.duration || 0;
        paintProgress(t, dur);
        const now = performance.now();
        if (now - lastStateAt > 250) {
          lastStateAt = now;
          setCurrent(t);
        }
      }
      if (!video.paused && !video.ended) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [paintProgress]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    bufferShownRef.current = 0;
    setCurrent(0);
    setDuration(0);
    paintProgress(0, 0);
    bufferRafRef.current = requestAnimationFrame(smoothBufferTick);

    const onMeta = () => {
      const dur = video.duration || 0;
      setDuration(dur);
      paintProgress(video.currentTime, dur);
    };
    const onDur = () => {
      const dur = video.duration || 0;
      setDuration(dur);
      paintProgress(video.currentTime, dur);
    };
    const onPlay = () => {
      setPlaying(true);
      startRaf();
      bumpUi();
    };
    const onPause = () => {
      setPlaying(false);
      stopRaf();
      const t = video.currentTime;
      const dur = video.duration || 0;
      setCurrent(t);
      paintProgress(t, dur);
      setShowUi(true);
      clearHideTimer();
    };
    const onVol = () => {
      setMuted(video.muted);
      setVolume(video.volume);
    };
    const onEnded = () => {
      setPlaying(false);
      stopRaf();
      setShowUi(true);
    };
    const onTime = () => {
      if (seekingRef.current) return;
      const t = video.currentTime;
      const dur = video.duration || 0;
      paintProgress(t, dur);
    };

    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("durationchange", onDur);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("volumechange", onVol);
    video.addEventListener("ended", onEnded);
    video.addEventListener("timeupdate", onTime);

    // Already playing after progressive remount / autoplay
    if (!video.paused) {
      setPlaying(true);
      setDuration(video.duration || 0);
      startRaf();
    } else if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      setDuration(video.duration || 0);
      paintProgress(video.currentTime, video.duration || 0);
    }

    return () => {
      stopRaf();
      if (bufferRafRef.current != null) {
        cancelAnimationFrame(bufferRafRef.current);
        bufferRafRef.current = null;
      }
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("durationchange", onDur);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("volumechange", onVol);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", onTime);
    };
  }, [bumpUi, paintProgress, smoothBufferTick, startRaf, src, playerEpoch]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play();
    else video.pause();
    bumpUi();
  };

  const seekFromClientX = (clientX: number, track: HTMLElement) => {
    const video = videoRef.current;
    if (!video) return;
    const dur = video.duration || duration;
    if (!dur || !Number.isFinite(dur)) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const t = ratio * dur;
    video.currentTime = t;
    setCurrent(t);
    setDuration(dur);
    paintProgress(t, dur);
  };

  const setControlsGesture = (active: boolean) => {
    onControlsGestureChange?.(active);
  };

  const onSeekPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const track = e.currentTarget;
    track.setPointerCapture(e.pointerId);
    seekingRef.current = true;
    setSeeking(true);
    setControlsGesture(true);
    seekFromClientX(e.clientX, track);
    bumpUi(true);
  };

  const onSeekPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!seekingRef.current) return;
    e.stopPropagation();
    seekFromClientX(e.clientX, e.currentTarget);
  };

  const onSeekPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!seekingRef.current) return;
    e.stopPropagation();
    seekFromClientX(e.clientX, e.currentTarget);
    seekingRef.current = false;
    setSeeking(false);
    setControlsGesture(false);
    bumpUi();
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
    bumpUi();
  };

  const onVolumeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const v = Number(e.target.value);
    video.volume = v;
    video.muted = v === 0;
    setVolume(v);
    setMuted(v === 0);
    bumpUi();
  };

  const toggleFullscreen = async () => {
    const el = wrapRef.current;
    if (!el) return;
    try {
      const active =
        document.fullscreenElement ||
        // @ts-expect-error webkit
        document.webkitFullscreenElement;
      if (active) {
        if (document.exitFullscreen) await document.exitFullscreen();
        // @ts-expect-error webkit
        else if (document.webkitExitFullscreen)
          // @ts-expect-error webkit
          await document.webkitExitFullscreen();
        setIsFullscreen(false);
      } else {
        if (el.requestFullscreen) await el.requestFullscreen();
        // @ts-expect-error webkit
        else if (el.webkitRequestFullscreen)
          // @ts-expect-error webkit
          await el.webkitRequestFullscreen();
        setIsFullscreen(true);
        requestAnimationFrame(() => fitVideoToStage());
      }
    } catch {
      /* ignore */
    }
    bumpUi();
  };

  const togglePip = async () => {
    const video = videoRef.current;
    if (!video || !document.pictureInPictureEnabled) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    } catch {
      /* ignore */
    }
    bumpUi();
  };

  const progress = duration > 0 ? (current / duration) * 100 : 0;
  const remaining = Math.max(0, duration - current);
  const volPct = Math.round((muted ? 0 : volume) * 100);

  const barBtn =
    "grid h-9 w-9 shrink-0 place-content-center rounded-full text-white/90 transition hover:bg-white/10";

  const videoClass = isFullscreen
    ? "media-lightbox-item lightbox-fs-video bg-black object-contain"
    : className;

  return (
    <div
      ref={wrapRef}
      className={`lightbox-video-shell relative flex w-auto max-w-full flex-col items-center ${
        isFullscreen ? "lightbox-fs-active h-full w-full" : ""
      }`}
      onMouseMove={() => bumpUi()}
      onTouchStart={() => bumpUi()}
    >
      <div
        ref={setStageNode}
        className={`lightbox-video-stage relative items-center justify-center ${
          isFullscreen ? "flex h-full w-full" : "inline-flex max-w-full"
        }`}
      >
        <video
          key={videoKey}
          ref={videoRef}
          className={videoClass}
          style={{
            ...(isFullscreen ? undefined : mediaStyle),
            minWidth:
              !isFullscreen && videoSize.w < 2 ? "min(70vw, 420px)" : undefined,
            minHeight:
              !isFullscreen && videoSize.h < 2 ? "min(50vh, 320px)" : undefined,
          }}
          playsInline
          preload="auto"
          poster={poster || undefined}
          onClick={togglePlay}
        />

        {loading && !playError && (
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/35"
            aria-hidden
          >
            <ClipLoader color="#ffffff" size={42} speedMultiplier={0.85} />
          </div>
        )}

        {playError && (
          <div
            className="absolute inset-x-3 bottom-3 z-20 rounded-xl border border-white/15 bg-black/80 px-4 py-3 text-center text-sm text-white shadow-xl backdrop-blur-md sm:inset-x-6"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {playError}
          </div>
        )}
      </div>

      {/* fixed-width bar — not tied to video aspect */}
      <div
        className={`lightbox-video-controls w-[min(100%,26rem)] shrink-0 transition-opacity duration-300 sm:w-[28rem] ${
          isFullscreen ? "" : "mt-3"
        } ${showUi ? "opacity-100" : "opacity-0"}`}
      >
        <div
          className="rounded-2xl border border-white/10 bg-black/45 px-3.5 py-2.5 shadow-lg backdrop-blur-xl"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {/* left volume · center play · right pip/fs/hd */}
          <div className="mb-2 grid grid-cols-[1fr_auto_1fr] items-center gap-1">
            <div className="flex min-w-0 items-center gap-1.5 justify-self-start">
              <button
                type="button"
                onClick={toggleMute}
                className="grid h-8 w-8 shrink-0 place-content-center rounded-full text-white/90 transition hover:bg-white/10"
                aria-label={muted || volume === 0 ? "Unmute" : "Mute"}
              >
                {muted || volume === 0 ? (
                  <FaVolumeXmark className="h-[15px] w-[15px]" />
                ) : (
                  <FaVolumeHigh className="h-[15px] w-[15px]" />
                )}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={muted ? 0 : volume}
                onChange={onVolumeChange}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setControlsGesture(true);
                }}
                onPointerUp={() => setControlsGesture(false)}
                onPointerCancel={() => setControlsGesture(false)}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                className="lightbox-vol-range h-1 w-14 shrink-0 cursor-pointer appearance-none rounded-full sm:w-16"
                style={{
                  background: `linear-gradient(to right, #fff ${volPct}%, rgba(255,255,255,0.25) ${volPct}%)`,
                }}
                aria-label="Volume"
              />
            </div>

            <button
              type="button"
              onClick={togglePlay}
              className="grid h-10 w-10 place-content-center rounded-full text-white transition hover:bg-white/10"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? (
                <FaPause className="h-5 w-5" />
              ) : (
                <FaPlay className="ml-0.5 h-5 w-5" />
              )}
            </button>

            <div className="flex items-center justify-self-end gap-0.5">
              <button
                type="button"
                onClick={toggleFullscreen}
                className={barBtn}
                aria-label="Fullscreen"
              >
                <RiExpandDiagonalLine className="h-[18px] w-[18px]" />
              </button>
              {typeof document !== "undefined" &&
                document.pictureInPictureEnabled && (
                  <button
                    type="button"
                    onClick={togglePip}
                    className={barBtn}
                    aria-label="Picture in picture"
                  >
                    <MdOutlinePictureInPictureAlt className="h-[18px] w-[18px]" />
                  </button>
                )}
              <span className="ml-0.5 select-none rounded-md border border-white/35 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white/90">
                HD
              </span>
            </div>
          </div>

          <div
            className="group/seek relative h-4 cursor-pointer touch-none"
            onPointerDown={onSeekPointerDown}
            onPointerMove={onSeekPointerMove}
            onPointerUp={onSeekPointerUp}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onPointerCancel={() => {
              seekingRef.current = false;
              setSeeking(false);
              setControlsGesture(false);
            }}
          >
            <div className="absolute left-0 right-0 top-1/2 h-[3px] -translate-y-1/2 overflow-hidden rounded-full bg-white/25">
              <div
                ref={bufferFillRef}
                className="absolute inset-y-0 left-0 bg-white/35 will-change-[width]"
                style={{ width: "0%" }}
              />
              <div
                ref={progressFillRef}
                className="absolute inset-y-0 left-0 bg-white will-change-[width]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div
              ref={knobRef}
              className={`absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow will-change-[left] transition-opacity ${
                seeking
                  ? "opacity-100"
                  : "opacity-0 group-hover/seek:opacity-100"
              }`}
              style={{ left: `${progress}%` }}
            />
          </div>

          <div className="mt-1 flex items-center justify-between text-[11px] tabular-nums tracking-wide text-white/80">
            <span ref={currentLabelRef}>{formatClock(current)}</span>
            <span ref={remainLabelRef}>−{formatClock(remaining)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
